import "@/assets/styles/index.scss";
import { createVuetify } from "vuetify";

import type { ThemeDefinition } from "vuetify";
import Color from "color";

const colors = {
  primary: "#35A7BE",
  blue150: "#007bff1f",
  blue950: "#4D55BC",
  gray: "#718096",
  gray50: "#FAFCFF",
  gray950: "#F5F6FD",
  purple950: "#4E33FF",
};

function applyColors() {
  Object.entries(colors).forEach(([key, value]) => {
    const keyHasInt = key.replaceAll(/[a-zA-Z]/g, "") !== "";
    if (keyHasInt) {
      key = `${key.replaceAll(/\d/g, "")}-${key.replaceAll(/\D/g, "")}`;
    }
    document.documentElement.style.setProperty(`--${key}`, value);
  });
}

const baseColors = {
  primary: {
    100: String(
      Color(colors.primary)
        .lighten(1 - 0.1)
        .hex(),
    ),
    200: String(
      Color(colors.primary)
        .lighten(1 - 0.2)
        .hex(),
    ),
    DEFAULT: Color(colors.primary).hex(),
  },
  blue: {
    150: Color(colors.blue150).hex(),
    950: Color(colors.blue950).hex(),
  },
  gray: {
    50: Color(colors.gray50).hex(),
    DEFAULT: Color(colors.gray).hex(),
    950: Color(colors.gray950).hex(),
  },
  purple: {
    950: Color(colors.purple950).hex(),
    light: "#F0F7FF",
  },
};

const darkBaseColors = {
  primary: {
    100: Color(colors.primary)
      .darken(1 - 0.1)
      .hex(),
    200: Color(colors.primary)
      .lighten(1 - 0.2)
      .hex(),
    DEFAULT: Color(colors.primary).hex(),
  },
  blue: {
    150: Color(colors.blue150).hex(),
    950: Color(colors.blue950).hex(),
  },
  gray: {
    50: Color(colors.gray50).hex(),
    DEFAULT: Color(colors.gray).hex(),
    950: Color(colors.gray950).hex(),
  },
  purple: {
    950: Color(colors.purple950).hex(),
  },
};

function processColorobject(
  tailwindColorPack: Record<string, object & { DEFAULT?: string }>,
) {
  return Object.entries(tailwindColorPack)
    .filter(([_, value]) => value.DEFAULT)
    .reduce<Record<string, string>>((acc, [key, value]) => {
      return Object.assign(acc, { [key]: value.DEFAULT! });
    }, {});
}
export const myVuetifyLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    ...processColorobject(baseColors),
    background: "#FAFCFF",
    surface: "#FFFFFF",
    "surface-bright": "#FFFFFF",
    "surface-light": "#EEEEEE",
    "surface-variant": "#424242",
    "on-surface-variant": "#EEEEEE",
    primary: String(process.env.NUXT_PUBLIC_COLOR_PRIMARY || "#35A7BE"),
  },
} as const;

export const myVuetifyDarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    ...processColorobject(darkBaseColors),
    background: "#0f172a",
    surface: "#1e293b",
    "surface-bright": "#293548",
    "surface-light": "#334155",
    "surface-variant": "#475569",
    "on-surface-variant": "#cbd5e1",
    primary: String(process.env.NUXT_PUBLIC_COLOR_PRIMARY || "#35A7BE"),
  },
} as const;

export default defineNuxtPlugin((nuxtApp) => {
  const appConfig = useAppConfig();

  if (import.meta.client) {
    applyColors();
  }

  const vuetify = createVuetify({
    ssr: true,
    theme: {
      defaultTheme: "defaultTheme",
      variations: {
        colors: [
          "primary",
          "grey",
          "info",
          "success",
          "warning",
          "error",
          "cyan",
        ],
        lighten: 5,
        darken: 5,
      },
      themes: {
        defaultTheme: {
          ...myVuetifyLightTheme,
        },
        darkTheme: {
          ...myVuetifyDarkTheme,
          colors: {
            ...appConfig.theme,
            ...myVuetifyDarkTheme.colors,
          },
        },
      },
    },
    display: {
      mobileBreakpoint: "sm",
    },
    icons: {
      defaultSet: "mdi",
    },
  });
  nuxtApp.vueApp.use(vuetify);
});
